import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Crown, Star, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/contexts/AuthContext';

export const SubscriptionUsageWidget: React.FC = () => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const { isAdmin } = useAuth();
  
  const subscriptionTier = isAdmin ? 'enterprise' : (subscription.subscription_tier as any) || 'basic';
  const maxConnections = subscriptionTier === 'basic' ? 5 : -1; // Premium et Enterprise = illimité

  // TODO: Remplacer par de vraies données de connexions depuis Supabase
  const connectionsUsed = 0; // Pas de données réelles disponibles
  const usagePercentage = maxConnections === -1 ? 0 : (connectionsUsed / maxConnections) * 100;

  const getTierInfo = () => {
    switch (subscriptionTier) {
      case 'premium':
        return {
          icon: <Star className="h-4 w-4" />,
          label: 'Premium',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          nextTier: 'Enterprise',
          nextPrice: '99€/mois',
          benefits: ['Connexions illimitées', 'Événements VIP', 'Support dédié']
        };
      case 'enterprise':
        return {
          icon: <Crown className="h-4 w-4" />,
          label: 'Enterprise',
          color: 'text-amber-600',
          bgColor: 'bg-amber-100',
          nextTier: null,
          nextPrice: null,
          benefits: ['Accès complet', 'Consultant dédié', 'API d\'intégration']
        };
      default:
        return {
          icon: <Zap className="h-4 w-4" />,
          label: 'Basic',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/50',
          nextTier: 'Premium',
          nextPrice: '59€/mois',
          benefits: ['50 connexions', 'Recommandations IA', 'Événements premium']
        };
    }
  };

  const tierInfo = getTierInfo();

  if (subscriptionTier === 'enterprise' || subscriptionTier === 'premium') {
    const isPremium = subscriptionTier === 'premium';
    
    return (
      <Card className={isPremium 
        ? "border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10" 
        : "border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/50"
      }>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Badge className={isPremium 
              ? "bg-gradient-to-r from-primary to-primary/80 text-white" 
              : "bg-gradient-to-r from-amber-500 to-amber-600 text-white"
            }>
              {tierInfo.icon}
              <span className="ml-1">{tierInfo.label}</span>
            </Badge>
          </div>
          <CardTitle className="text-lg">
            {isPremium ? 'Accès Premium Débloqué' : 'Accès Complet Débloqué'}
          </CardTitle>
          <CardDescription>
            {isPremium 
              ? 'Vous avez accès à toutes les fonctionnalités premium de Leaders'
              : 'Vous avez accès à toutes les fonctionnalités enterprise de Leaders'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className={`text-lg font-bold ${isPremium ? 'text-primary' : 'text-amber-600'}`}>∞</p>
              <p className="text-xs text-muted-foreground">Connexions</p>
            </div>
            <div>
              <p className={`text-lg font-bold ${isPremium ? 'text-primary' : 'text-amber-600'}`}>
                {isPremium ? 'Premium' : 'VIP'}
              </p>
              <p className="text-xs text-muted-foreground">Événements</p>
            </div>
            <div>
              <p className={`text-lg font-bold ${isPremium ? 'text-primary' : 'text-amber-600'}`}>
                {isPremium ? 'Pro' : '24/7'}
              </p>
              <p className="text-xs text-muted-foreground">Support</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <Badge className={tierInfo.bgColor + ' ' + tierInfo.color}>
            {tierInfo.icon}
            <span className="ml-1">Plan {tierInfo.label}</span>
          </Badge>
          {usagePercentage > 80 && (
            <Badge variant="destructive" className="text-xs">
              Limite atteinte
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg">Usage de votre plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {maxConnections !== -1 && (
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>Connexions utilisées</span>
              <span>{connectionsUsed}/{maxConnections}</span>
            </div>
            <Progress value={usagePercentage} className="h-2" />
          </div>
        )}
        
        {tierInfo.nextTier && (
          <div className={`${tierInfo.bgColor} rounded-lg p-4`}>
            <h4 className="font-medium mb-2">Passez à {tierInfo.nextTier}</h4>
            <ul className="text-sm space-y-1 mb-3">
              {tierInfo.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary"></div>
                  {benefit}
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => navigate('/subscription')}
              size="sm" 
              className="w-full gap-2"
            >
              Passer à {tierInfo.nextTier} - {tierInfo.nextPrice}
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};